import { ErrorBoundary } from '@shared/layouts/ErrorBoundary';
import { NotFoundPage } from '@shared/layouts/NotFoundPage';
import { RootLayout } from '@shared/layouts/RootLayout';
import { MobileFallback } from '@shared/layouts/MobileFallback';
import { RouterProvider, useRouter } from '@shared/router';
import { ProtectedRoute } from '@shared/components/ProtectedRoute';
import { MmtHeader } from '@shared/components/MmtHeader';
import { ROUTES } from '@shared/constants';
import { lazy, StrictMode, Suspense, useEffect, useRef, type FC } from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { store } from './store/store';
import { useAppDispatch } from './store/hooks';
import { checkAuthStatus } from './features/auth/redux';
import './index.css';
import { Onboarding } from './features/onboarding/Onboarding';
import type { Route } from '@shared/router/types';

/**
 * Dispatches auth check once on app load.
 * Ref guard prevents StrictMode's double-invocation from firing
 * two concurrent JWT exchanges (the second would fail if the
 * token is single-use, and its failure would overwrite the first's
 * success in Redux).
 */
function AuthInitializer({ children }: { children: React.ReactNode }) {
  const dispatch = useAppDispatch();
  const dispatched = useRef(false);

  useEffect(() => {
    if (dispatched.current) return;
    dispatched.current = true;
    void dispatch(checkAuthStatus());
  }, [dispatch]);

  return <>{children}</>;
}

// Lazy load pages

const ChatPage = lazy(() =>
  import('@features/chat/ChatPage').then((m) => ({ default: m.ChatPage })),
);

const ChatThankYouPage = lazy(() =>
  import('@features/chat/ChatThankYouPage').then((m) => ({ default: m.ChatThankYouPage })),
);

// Route configuration (all routes include /myra base path)
// Public routes - accessible without authentication
const publicRoutes: Partial<Record<Route, FC>> = {
  [ROUTES.HOME]: Onboarding, // Landing page with login at /myra
};

// Protected routes - require authentication
const protectedRoutes: Partial<Record<Route, FC>> = {
  [ROUTES.CHAT]: ChatPage, // /myra/chat
  [ROUTES.CHAT_THANKYOU]: ChatThankYouPage, // /myra/chat/thankyou
};

function AppContent() {
  const { currentRoute } = useRouter();

  // Check if route is public or protected
  const PublicPageComponent = publicRoutes[currentRoute];
  const ProtectedPageComponent = protectedRoutes[currentRoute];

  // Handle 404
  if (!PublicPageComponent && !ProtectedPageComponent) {
    return (
      <RootLayout>
        <NotFoundPage />
      </RootLayout>
    );
  }

  // Render public route
  if (PublicPageComponent) {
    return (
      <RootLayout>
        <Suspense
          fallback={
            <div className="flex min-h-[60vh] items-center justify-center">
              <div className="text-center">
                <div className="border-primary mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-b-2" />
                <p className="text-medium-emphasis">Loading...</p>
              </div>
            </div>
          }
        >
          <PublicPageComponent />
        </Suspense>
      </RootLayout>
    );
  }

  // Render protected route
  return (
    <RootLayout>
      <ProtectedRoute>
        <Suspense
          fallback={
            <div className="flex min-h-[60vh] items-center justify-center">
              <div className="text-center">
                <div className="border-primary mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-b-2" />
                <p className="text-medium-emphasis">Loading...</p>
              </div>
            </div>
          }
        >
          {ProtectedPageComponent && <ProtectedPageComponent />}
        </Suspense>
      </ProtectedRoute>
    </RootLayout>
  );
}

function App() {
  return (
    <>
      {/* Mobile / tablet fallback (below 1024px) */}
      <div className="lg:hidden">
        <MobileFallback />
      </div>

      {/* Desktop app (1024px and above) */}
      <div className="hidden lg:block">
        <Provider store={store}>
          <ErrorBoundary>
            <AuthInitializer>
              <MmtHeader />
              <RouterProvider>
                <AppContent />
              </RouterProvider>
            </AuthInitializer>
          </ErrorBoundary>
        </Provider>
      </div>
    </>
  );
}

const rootElement = document.getElementById('root');

if (!rootElement) {
  throw new Error('Failed to find the root element');
}

createRoot(rootElement).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
